// features/videos/railRanking.ts — pure selection logic for the video watch page's
// "up next" rail. Kept separate from the WatchRail component so the ranking rules
// are unit-testable without rendering. (Named distinctly from WatchRail.tsx —
// case-only filenames collide on Windows/macOS case-insensitive filesystems.)
import type { VideoItem } from '@/core/models/video';

/**
 * Trending group: items with a trendingRank, ascending, excluding the current
 * video and Shorts — a Short links to the swipe player (/videos/shorts/:id),
 * not this 16:9 watch page, so it can't be a rail destination here (matches
 * VideosPage's `!v.short` filter for its own "For you"/"Trending now" rows).
 */
export function trendingForRail(videos: VideoItem[], currentId: string): VideoItem[] {
  return videos
    .filter((v) => v.id !== currentId && !v.short && v.trendingRank !== undefined)
    .sort((a, b) => a.trendingRank! - b.trendingRank!);
}

/**
 * Suggested group: same category as the current video first, then videos sharing
 * at least one tag, then the rest — each bucket ordered most-recent-first (`ts`
 * descending). Excludes the current video, anything already surfaced in the
 * trending group (so the same video doesn't appear twice in the rail), and
 * Shorts (see trendingForRail's note — wrong player, so not a rail destination).
 */
export function suggestedForRail(
  videos: VideoItem[],
  current: VideoItem,
  trending: VideoItem[],
  limit = 8,
): VideoItem[] {
  const exclude = new Set([current.id, ...trending.map((v) => v.id)]);
  const pool = videos.filter((v) => !v.short && !exclude.has(v.id));

  const sameCategory = pool.filter((v) => v.category === current.category);
  const sharedTag = pool.filter(
    (v) => v.category !== current.category && v.tags.some((t) => current.tags.includes(t)),
  );
  const rest = pool.filter(
    (v) => v.category !== current.category && !v.tags.some((t) => current.tags.includes(t)),
  );

  const byRecency = (list: VideoItem[]) => [...list].sort((a, b) => b.ts - a.ts);

  return [...byRecency(sameCategory), ...byRecency(sharedTag), ...byRecency(rest)].slice(0, limit);
}
